import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import "../styles/AdminTestimonials.css";

type Status = "active" | "inactive";
type ModalMode = "add" | "edit" | "view";

interface SoftwareOption {
  id: number;
  title: string;
}

interface SoftwareModuleRow {
  id: number;
  software_id: number;
  software_title: string;
  title: string;
  description: string;
  image: any;
  link: string;
  status: Status;
  createdAt: string;
  updatedAt: string;
}

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

export default function AdminSoftwareModules() {
  const { showModal } = useMessageModal();

  const [data, setData] = useState<SoftwareModuleRow[]>([]);
  const [softwareList, setSoftwareList] = useState<SoftwareOption[]>([]);
  const [selectedRow, setSelectedRow] = useState<SoftwareModuleRow | null>(
    null,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const moduleFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      {
        key: "software_id",
        label: "Software",
        type: "select",
        options: softwareList.map((item) => ({
          label: item.title,
          value: item.id,
        })),
        colSpan: 2,
      },
      {
        key: "title",
        label: "Module Title",
        type: "text",
        colSpan: 2,
      },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "image",
        label: "Module Image",
        type: "image",
        imageType: "single",
        imageFolder: "software-modules",
        uploadField: "moduleImage",
        colSpan: 1,
      },
      {
        key: "link",
        label: "Page Link",
        type: "text",
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 1,
      },
    ],
    [softwareList],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "software_title", label: "Software" },
    { key: "title", label: "Module Title" },
    {
      key: "image",
      label: "Image",
      imageFolder: "software-modules",
    },
    {
      key: "description",
      label: "Description",
      render: (row) => (
        <span className="admin-testimonial-text-clamp">
          {row.description || "-"}
        </span>
      ),
    },
    {
      key: "link",
      label: "Link",
      render: (row) =>
        row.link ? (
          <a href={row.link} target="_blank" rel="noopener noreferrer">
            {row.link}
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
    },
  ];

  const fetchSoftware = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/software`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        setSoftwareList(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Software Error:", error);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/software/modules`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Software Modules Error:", error);
    }
  };

  useEffect(() => {
    fetchSoftware();
    fetchModules();
  }, []);

  const buildFormData = (formData: any, fields: FormField[]) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.key === "id") return;

      if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value?.removed || value === "") {
          form.append(field.key, "REMOVE");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    return form;
  };

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, moduleFields);

      const method = modalMode === "add" ? "POST" : "PUT";
      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/software/modules`
          : `${BASE_URL}/admin/software/modules/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (result.success) {
        fetchModules();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Software module added successfully."
              : "Software module updated successfully.",
          confirmText: "OK",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save software module.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Submit Software Module Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Something went wrong while saving software module.",
        confirmText: "OK",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Software Module",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/software/modules/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchModules();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Software module deleted successfully.",
              confirmText: "OK",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Failed to delete software module.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Software Module Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting software module.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  return (
    <div>
      <AdminListToolkit
        title="Software Modules"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="software_modules"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Software Module"
        fields={moduleFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
